import { useEffect, useCallback, useRef, useMemo, useState, memo } from 'react';
import { MapContainer, GeoJSON, useMap } from 'react-leaflet';
import L from 'leaflet';
import type { Map as LeafletMap } from 'leaflet';
import styled from '@emotion/styled';
import { theme, scaled } from '../styles/theme';
import { getTypeColor, ALL_INVESTMENT_TYPES, PHYSICAL_TYPES, FINANCIAL_TYPES } from '../utils/colors';
import { volumeToMillions, formatInvestmentVolume } from '../utils/formatters';
import { exportMapImage } from '../utils/mapImageExport';
import { GlBasemapLayer } from './GlBasemapLayer';
import { industryLabel, otherLabel, stateLabel, t, typeLabel, useT } from '../i18n';
import type { MapPoint, WahlkreiseGeoJson } from '../types';

interface AggregateCluster {
  key: string;
  type: string;
  lat: number;
  lng: number;
  totalVolume: number;
  count: number;
  points: MapPoint[];
}

const STATE_ZOOM_THRESHOLD = 9;

// Touch / coarse-pointer detection. Mirrors the (hover: none)/(pointer: coarse)
// guard the bar chart uses: hover-driven affordances (sticky tooltips,
// one-finger map drag) misbehave on touch and need a tap-friendly fallback.
function isCoarsePointer(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(hover: none), (pointer: coarse)').matches;
}

function clusterByTypeAndGrid(
  points: MapPoint[],
  zoom: number,
): { aggregates: AggregateCluster[]; singles: MapPoint[] } {
  const gridSize = 360 / Math.pow(2, zoom);
  const groups = new Map<string, MapPoint[]>();

  for (const p of points) {
    const type = p.type_of_investment || 'Other';
    // Zoomed out: group by federal state; zoomed in: group by grid cell
    const locationKey = zoom < STATE_ZOOM_THRESHOLD
      ? (p.federal_state || `${Math.floor(p.longitude / gridSize)}::${Math.floor(p.latitude / gridSize)}`)
      : `${Math.floor(p.longitude / gridSize)}::${Math.floor(p.latitude / gridSize)}`;
    const key = `${type}::${locationKey}`;
    let arr = groups.get(key);
    if (!arr) {
      arr = [];
      groups.set(key, arr);
    }
    arr.push(p);
  }

  const aggregates: AggregateCluster[] = [];
  const singles: MapPoint[] = [];

  for (const [key, pts] of groups) {
    if (pts.length === 1) {
      singles.push(pts[0]!);
    } else {
      aggregates.push({
        key,
        type: pts[0]!.type_of_investment || 'Other',
        lat: pts.reduce((s, p) => s + p.latitude, 0) / pts.length,
        lng: pts.reduce((s, p) => s + p.longitude, 0) / pts.length,
        totalVolume: pts.reduce((s, p) => s + (p.investment_volume_value_eur ?? 0), 0),
        count: pts.length,
        points: pts,
      });
    }
  }
  return { aggregates, singles };
}

// Simple GeoJSON type to avoid dependency on @types/geojson
type GeoJsonFeatureCollection = {
  type: 'FeatureCollection';
  features: Array<{
    type: 'Feature';
    properties: Record<string, unknown>;
    geometry: Record<string, unknown>;
  }>;
};

const MapWrapper = styled.div<{ $fullscreen?: boolean }>`
  position: relative;
  /* z-index:0 + isolation create a stacking context that contains Leaflet's
     own z-index scale (panes 200-700, controls 1000) plus our legends and
     tooltip. At page level the whole map subtree stacks as a single layer
     at 0, so those large internal values can never overlay the host nav. */
  z-index: 0;
  isolation: isolate;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.lg};
  /* Flat on the page background — no card shadow. */
  overflow: hidden;
  height: 100%;
  min-height: 500px;

  .leaflet-container {
    height: 100%;
    min-height: 500px;
    border-radius: ${theme.borderRadius.lg};
  }

  /* Financial-transaction bubbles are hover-only (anonymised, no detail
     modal), so they must not look clickable. */
  .leaflet-interactive.financial-bubble {
    cursor: default;
  }

  /* Leaflet's own attribution is rem-based (0.75rem), so on the host's 13px
     root it drops to 9.8px. It carries the OpenStreetMap credit, which has to
     stay readable — scale it like everything else in the widget. */
  .leaflet-control-attribution {
    font-size: ${scaled(11)};
  }

  /* Mobile fullscreen: pin to the viewport and fill it. MapWrapper has no
     transform/filter, so position:fixed resolves against the viewport even
     though isolation:isolate is set (isolation creates a stacking context,
     not a containing block). The subtree still stacks inside that context, so
     the overlay can't leak above the host nav — matching the isolation intent.
     MapFitController's ResizeObserver fires on the size change and calls
     invalidateSize(), so the panes re-lay-out to the new box automatically. */
  ${p => p.$fullscreen && `
    position: fixed;
    inset: 0;
    z-index: 1100;
    min-height: 0;
    border-radius: 0;
    .leaflet-container {
      min-height: 0;
      border-radius: 0;
    }
  `}

  /* Zoom in/out control restyled to match the widget's components: sans-m
     glyphs, no borders/outlines, and the +/- pair wrapped in a single
     --radius-m rounded group (overflow:hidden clips the button corners). */
  .leaflet-control-zoom {
    border: none;
    border-radius: var(--radius-m);
    overflow: hidden;
    box-shadow: var(--shadow-s);
  }
  .leaflet-control-zoom a {
    font-family: var(--sans-stack);
    font-size: ${scaled(16)};
    color: var(--color-text-default);
    background: var(--color-bg-surface);
    border: none;
    border-radius: 0;
  }
  .leaflet-control-zoom a + a {
    border-top: 1px solid var(--color-bg-muted);
  }
  .leaflet-control-zoom a:hover {
    background: var(--color-bg-muted);
    color: var(--color-text-default);
  }
`;

// Mobile-only fullscreen toggle. Top-right is the only free map corner
// (Leaflet's zoom control owns top-left; the two legends own the bottom
// corners). Styled to match the restyled .leaflet-control-zoom buttons and
// pinned at the legends' z-index (1000) inside MapWrapper's isolated context.
const FullscreenButton = styled.button`
  display: none;
  position: absolute;
  top: 12px;
  right: 12px;
  z-index: 1000;
  width: 34px;
  height: 34px;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  color: var(--color-text-default);
  background: var(--color-bg-surface);
  border: none;
  border-radius: var(--radius-m);
  box-shadow: var(--shadow-s);
  font-family: var(--sans-stack);

  &:hover {
    background: var(--color-bg-muted);
  }

  svg {
    width: 18px;
    height: 18px;
    display: block;
  }

  /* Only meaningful where the layout collapses to a single column and the map
     is cramped — matches the breakpoint used across the widget. */
  @media (max-width: 1024px) {
    display: flex;
  }
`;

// Same affordance as the table header's export trigger (bordered square
// download-icon button), but this one downloads the map itself as a PNG in
// its current view. Pinned to the map's free top-right corner, with a solid
// surface background so it stays readable over the tiles. On mobile the
// fullscreen toggle owns that spot, so drop below it.
const MapExportButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  /* Inside MapWrapper's isolated context — same rank as the legends. */
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  font-size: 18px;
  padding: 0;
  /* Clip the icon-font ligature fallback, see the table's ExportButton. */
  overflow: hidden;
  border: 1px solid ${theme.colors.border};
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: all 0.15s ease;

  &:hover {
    background: rgba(24, 28, 68, 0.05);
    border-color: ${theme.colors.text.secondary};
    color: ${theme.colors.text.primary};
  }

  @media (max-width: 1024px) {
    top: 54px;
  }
`;

const LegendContainer = styled.div`
  position: absolute;
  bottom: 24px;
  right: 12px;
  /* Inside MapWrapper's isolated context — must outrank Leaflet's internal
     scale (panes 200-700, controls 1000); never leaks to page level. */
  z-index: 1000;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadow.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};

  /* On a phone-width map the type legend (right) and volume legend (left) are
     both pinned to the bottom corners and collide in the middle, covering most
     of an already-small map. Shrink + cap each to its half so they stay in
     their corners; long type labels wrap rather than push into the gap. */
  @media (max-width: 600px) {
    bottom: 8px;
    right: 8px;
    max-width: 45%;
    padding: 5px 7px;
    font-size: ${scaled(11)};
  }
`;

const LegendTitle = styled.div`
  font-weight: ${theme.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;

// Header row that carries the legend title and, on phones only, a tap target to
// collapse/expand the body. On desktop it's an inert wrapper (the chevron is
// hidden and the body is always shown), so the legends behave exactly as before.
const LegendHeader = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  width: 100%;
  margin: 0;
  padding: 0;
  background: none;
  border: none;
  font: inherit;
  color: inherit;
  text-align: left;
  cursor: default;

  @media (max-width: 600px) {
    cursor: pointer;
  }
`;

const LegendChevron = styled.span<{ $open: boolean }>`
  display: none;

  /* The collapse affordance only exists where the legends would otherwise
     crowd the map — phones. Rotates to point up when the body is open. */
  @media (max-width: 600px) {
    display: inline-block;
    flex-shrink: 0;
    font-size: ${scaled(11.2)};
    line-height: 1;
    color: ${theme.colors.text.secondary};
    transition: transform 0.2s ease;
    transform: rotate(${p => (p.$open ? '180deg' : '0deg')});
  }
`;

// Body holding the legend rows. Desktop always shows it (with the title→body
// gap the old margin-bottom used to provide); phones hide it when collapsed so
// the legend shrinks to a single-line chip in its corner.
const LegendBody = styled.div<{ $open: boolean }>`
  margin-top: ${theme.spacing.xs};

  @media (max-width: 600px) {
    display: ${p => (p.$open ? 'block' : 'none')};
    margin-top: ${p => (p.$open ? '4px' : '0')};
  }
`;

const LegendItem = styled.div<{ dimmed?: boolean }>`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 0;
  color: ${theme.colors.text.secondary};
  cursor: pointer;
  opacity: ${p => p.dimmed ? 0.35 : 1};
  transition: opacity 0.15s;

  &:hover {
    opacity: ${p => p.dimmed ? 0.6 : 0.8};
  }
`;

const LegendDot = styled.span<{ color: string }>`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: ${p => p.color};
  flex-shrink: 0;
`;

const ChoroplethLegendContainer = styled.div`
  position: absolute;
  bottom: 24px;
  left: 12px;
  /* Inside MapWrapper's isolated context — see LegendContainer. */
  z-index: 1000;
  background: ${theme.colors.surface};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadow.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-family: ${theme.fontFamily};
  font-size: ${theme.fontSize.xs};

  /* Mirror LegendContainer's mobile shrink so the two corner legends don't
     overlap on a narrow map. */
  @media (max-width: 600px) {
    bottom: 8px;
    left: 8px;
    max-width: 45%;
    padding: 5px 7px;
    font-size: ${scaled(11)};
  }
`;

const LegendSwatch = styled.span<{ bg: string }>`
  width: 16px;
  height: 12px;
  border-radius: 2px;
  background: ${p => p.bg};
  border: 1px solid rgba(24, 28, 68, 0.2);
  flex-shrink: 0;
`;

interface WahlkreisStats {
  count: number;
  totalVolume: number;
}

const CHOROPLETH_OPACITIES = [0.06, 0.14, 0.24, 0.36, 0.50, 0.65, 0.82];

interface ChoroplethStep {
  min: number;
  max: number;
  color: string;
  label: string;
}

function compactVolume(value: number): string {
  const u = t().map.compactVolume;
  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return u.bn((value / 1_000_000_000).toFixed(1));
  if (abs >= 1_000_000) return u.m((value / 1_000_000).toFixed(0));
  if (abs >= 1_000) return u.k((value / 1_000).toFixed(0));
  return `${value}`;
}

function computeEquidistantSteps(stats: Map<number, WahlkreisStats>): ChoroplethStep[] {
  const volumes = [...stats.values()].map(s => s.totalVolume).filter(v => v > 0);
  if (volumes.length === 0) return [];

  const minVol = Math.min(...volumes);
  const maxVol = Math.max(...volumes);
  if (maxVol <= 0) return [];

  const numBins = CHOROPLETH_OPACITIES.length;
  // Logarithmic breaks — equidistant on log scale to handle skewed distributions
  const logMin = Math.log10(Math.max(1, minVol));
  const logMax = Math.log10(maxVol);
  const logStep = (logMax - logMin) / numBins;

  const steps: ChoroplethStep[] = [];
  for (let i = 0; i < numBins; i++) {
    const min = i === 0 ? 1 : Math.round(Math.pow(10, logMin + i * logStep));
    const max = Math.round(Math.pow(10, logMin + (i + 1) * logStep));
    if (min >= max && i < numBins - 1) continue;
    const opacity = CHOROPLETH_OPACITIES[Math.min(steps.length, CHOROPLETH_OPACITIES.length - 1)]!;
    const isLast = i === numBins - 1;
    steps.push({
      min,
      max: isLast ? Infinity : max,
      color: `rgba(24, 28, 68, ${opacity})`,
      label: isLast ? `${compactVolume(min)}+` : `${compactVolume(min)}–${compactVolume(max)}`,
    });
  }

  return steps;
}

function getChoroplethColor(count: number, steps: ChoroplethStep[]): string {
  if (count <= 0) return 'transparent';
  for (const step of steps) {
    if (count >= step.min && count <= step.max) return step.color;
  }
  return steps.length > 0 ? steps[steps.length - 1]!.color : 'transparent';
}

// Ray-casting point-in-polygon (coordinates in [lng, lat] GeoJSON order)
function pointInRing(lng: number, lat: number, ring: number[][]): boolean {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i]![0]!, yi = ring[i]![1]!;
    const xj = ring[j]![0]!, yj = ring[j]![1]!;
    if ((yi > lat) !== (yj > lat) && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInGeometry(lng: number, lat: number, geometry: { type: string; coordinates: number[][][] | number[][][][] }): boolean {
  if (geometry.type === 'Polygon') {
    const coords = geometry.coordinates as number[][][];
    if (!pointInRing(lng, lat, coords[0]!)) return false;
    // Check holes
    for (let i = 1; i < coords.length; i++) {
      if (pointInRing(lng, lat, coords[i]!)) return false;
    }
    return true;
  }
  if (geometry.type === 'MultiPolygon') {
    const coords = geometry.coordinates as number[][][][];
    for (const polygon of coords) {
      if (!pointInRing(lng, lat, polygon[0]!)) continue;
      let inHole = false;
      for (let i = 1; i < polygon.length; i++) {
        if (pointInRing(lng, lat, polygon[i]!)) { inHole = true; break; }
      }
      if (!inHole) return true;
    }
  }
  return false;
}

interface WahlkreisBBox { minLng: number; maxLng: number; minLat: number; maxLat: number; }

function computeBBox(geometry: { type: string; coordinates: number[][][] | number[][][][] }): WahlkreisBBox {
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  const rings = geometry.type === 'MultiPolygon'
    ? (geometry.coordinates as number[][][][]).flatMap(p => p[0]!)
    : (geometry.coordinates as number[][][])[0]!;
  for (const [lng, lat] of rings) {
    if (lng! < minLng) minLng = lng!;
    if (lng! > maxLng) maxLng = lng!;
    if (lat! < minLat) minLat = lat!;
    if (lat! > maxLat) maxLat = lat!;
  }
  return { minLng, maxLng, minLat, maxLat };
}

// Bounding box of Germany (SW corner, NE corner). The map is framed to these
// bounds instead of a fixed zoom level so the whole country stays visible at
// any map size: fixed zoom 6 was tuned for the wide desktop column and looked
// zoomed-in / cropped on the much narrower mobile map. Exported so the widget's
// "reset view" / "clear all" actions reuse the same framing.
export const GERMANY_BOUNDS: L.LatLngBoundsExpression = [[47.27, 5.87], [55.06, 15.04]];

const geoJsonStyle = {
  fillColor: 'transparent',
  fillOpacity: 0,
  color: '#181C44',
  weight: 1.5,
  opacity: 0.3,
};

function MapControllerSimple({ points, selectedClusterId, selectionSourceRef }: { points: MapPoint[]; selectedClusterId: number | null; selectionSourceRef: React.MutableRefObject<'bubble' | 'table' | null> }) {
  const map = useMap();
  const prevClusterIdRef = useRef<number | null>(null);

  useEffect(() => {
    // Only auto-zoom when the selection came from a map bubble click
    if (selectionSourceRef.current !== 'bubble') {
      prevClusterIdRef.current = selectedClusterId;
      return;
    }

    if (selectedClusterId == null) {
      if (prevClusterIdRef.current != null) {
        map.flyToBounds(GERMANY_BOUNDS, { duration: 0.8 });
      }
      prevClusterIdRef.current = null;
      return;
    }
    if (selectedClusterId === prevClusterIdRef.current) return;
    prevClusterIdRef.current = selectedClusterId;

    const matching = points.filter(p => p.cluster_id === selectedClusterId);
    if (matching.length === 0) return;

    if (matching.length === 1) {
      map.flyTo([matching[0]!.latitude, matching[0]!.longitude], 9, { duration: 0.8 });
    } else {
      const lats = matching.map(p => p.latitude);
      const lngs = matching.map(p => p.longitude);
      map.flyToBounds(
        [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]],
        { padding: [50, 50], duration: 0.8 }
      );
    }
  }, [selectedClusterId, points, map, selectionSourceRef]);

  return null;
}

// Leaflet reads the container size at init, but the map lives in a CSS grid
// whose height only resolves after layout. So the one-shot `bounds` fit on
// <MapContainer> runs against a stale (too-short) size, which both clips the
// choropleth/bubble panes at the bottom and over-zooms-out the default view.
// This controller calls invalidateSize() + re-fits GERMANY_BOUNDS once the
// container has its real dimensions (and on later resizes), so the panes cover
// the full height and the framing is computed against the true aspect ratio.
// A user-interaction guard stops it from yanking the view after a manual
// zoom/pan; a programmatic flag keeps our own fit from tripping that guard.
function MapFitController() {
  const map = useMap();
  const userInteractedRef = useRef(false);
  const programmaticRef = useRef(false);

  useEffect(() => {
    const refit = () => {
      map.invalidateSize();
      if (userInteractedRef.current) return;
      const size = map.getSize();
      if (size.x === 0 || size.y === 0) return;
      programmaticRef.current = true;
      map.fitBounds(GERMANY_BOUNDS, { padding: [8, 8], animate: false });
    };

    const onInteractStart = () => {
      // A programmatic fit also fires zoomstart/dragstart-adjacent events; only
      // flag genuine user gestures so resizes keep auto-fitting until then.
      if (!programmaticRef.current) userInteractedRef.current = true;
    };
    const onMoveEnd = () => { programmaticRef.current = false; };

    map.on('dragstart', onInteractStart);
    map.on('zoomstart', onInteractStart);
    map.on('moveend', onMoveEnd);

    const ro = new ResizeObserver(() => refit());
    ro.observe(map.getContainer());
    // Initial pass in case the observer's first callback lands before layout.
    refit();

    return () => {
      ro.disconnect();
      map.off('dragstart', onInteractStart);
      map.off('zoomstart', onInteractStart);
      map.off('moveend', onMoveEnd);
    };
  }, [map]);

  return null;
}

/**
 * Touch scroll-trap guard (#1). On coarse pointers the 500px-tall map sits in
 * the middle of a vertically-scrolling embed; with Leaflet's default one-finger
 * drag-pan, a page swipe that lands on the map gets swallowed and the user is
 * stuck panning instead of scrolling past. Fix:
 *   - touch-action: pan-y lets the browser scroll the page on a one-finger
 *     vertical drag, so the page never traps;
 *   - dragging is disabled (one finger no longer pans the map);
 *   - touchZoom stays on, so two fingers pinch-zoom AND pan the map.
 * A brief hint surfaces only on a one-finger *horizontal* drag (a clear attempt
 * to pan the map, not a vertical page scroll). Desktop is untouched.
 */
function TouchGestureGuard({ fullscreen }: { fullscreen: boolean }) {
  const map = useMap();
  useEffect(() => {
    if (!isCoarsePointer()) return;

    const container = map.getContainer();

    // In fullscreen the map fills the viewport and background scroll is locked,
    // so there's no page to trap — the scroll guard isn't needed and the
    // two-finger-only constraint feels broken at full size. Hand full gesture
    // control to Leaflet (one-finger pan + pinch). Restored to the guarded
    // behaviour below when fullscreen exits (effect re-runs on the prop change).
    if (fullscreen) {
      const prev = container.style.touchAction;
      map.dragging.enable();
      container.style.touchAction = 'none';
      return () => { container.style.touchAction = prev; };
    }

    map.dragging.disable();
    const prevTouchAction = container.style.touchAction;
    // One finger → `pan-y` so the browser scrolls the page (never trapped).
    // Two fingers → we switch to `none` (below) so Leaflet owns the pinch.
    container.style.touchAction = 'pan-y';

    const hint = document.createElement('div');
    hint.textContent = t().map.twoFingers;
    hint.style.cssText = `
      position: absolute; inset: 0; z-index: 1200;
      display: flex; align-items: center; justify-content: center;
      text-align: center; padding: 24px; box-sizing: border-box;
      background: rgba(24, 28, 68, 0.45); color: #fff;
      font-family: ${theme.fontFamily}; font-size: 15px; font-weight: 600;
      opacity: 0; pointer-events: none; transition: opacity 0.2s;
    `;
    container.appendChild(hint);

    let hideTimer: ReturnType<typeof setTimeout> | undefined;
    let startX = 0;
    let startY = 0;

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length >= 2) {
        // Second finger landed: this is a pinch. iOS Safari ignores
        // `touch-action: pan-y` for pinch and would zoom the whole page
        // instead of the map, so the Leaflet map never zooms and the bubbles
        // never re-aggregate. Drop to `none` for the gesture's duration so
        // Leaflet's touchZoom owns the pinch; restored on touchend below.
        container.style.touchAction = 'none';
        hint.style.opacity = '0';
      } else if (e.touches.length === 1) {
        startX = e.touches[0]!.clientX;
        startY = e.touches[0]!.clientY;
      }
    };
    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length !== 1) return;
      const dx = Math.abs(e.touches[0]!.clientX - startX);
      const dy = Math.abs(e.touches[0]!.clientY - startY);
      // Horizontal-dominant single-finger drag = an attempt to pan the map
      // (vertical scroll is the browser's job now). Teach the two-finger gesture.
      if (dx > 12 && dx > dy) {
        hint.style.opacity = '1';
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => { hint.style.opacity = '0'; }, 1100);
      }
    };
    // Once the pinch ends (back below two fingers), restore `pan-y` so a
    // subsequent one-finger drag scrolls the page again instead of being
    // swallowed by the map.
    const onTouchEnd = (e: TouchEvent) => {
      if (e.touches.length < 2) container.style.touchAction = 'pan-y';
    };

    container.addEventListener('touchstart', onTouchStart, { passive: true });
    container.addEventListener('touchmove', onTouchMove, { passive: true });
    container.addEventListener('touchend', onTouchEnd, { passive: true });
    container.addEventListener('touchcancel', onTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('touchstart', onTouchStart);
      container.removeEventListener('touchmove', onTouchMove);
      container.removeEventListener('touchend', onTouchEnd);
      container.removeEventListener('touchcancel', onTouchEnd);
      if (hideTimer) clearTimeout(hideTimer);
      hint.remove();
      container.style.touchAction = prevTouchAction;
      map.dragging.enable();
    };
  }, [map, fullscreen]);

  return null;
}

/** Imperative Wahlkreise choropleth layer — visual GeoJSON + map-level event handling */
function WahlkreiseChoropleth({ wahlkreiseData, stats, steps, clusterIdsByWahlkreis, selectedWahlkreis, onWahlkreisClick, bubbleHoveredRef }: {
  wahlkreiseData: WahlkreiseGeoJson;
  stats: Map<number, WahlkreisStats>;
  steps: ChoroplethStep[];
  clusterIdsByWahlkreis: Map<number, number[]>;
  selectedWahlkreis: { wkrNr: number; name: string } | null;
  onWahlkreisClick: (wkrNr: number, name: string, clusterIds: number[]) => void;
  bubbleHoveredRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);
  const statsRef = useRef<Map<number, WahlkreisStats>>(new Map());
  const stepsRef = useRef<ChoroplethStep[]>([]);
  const clusterIdsRef = useRef(clusterIdsByWahlkreis);
  const onClickRef = useRef(onWahlkreisClick);
  const tooltipRef = useRef<L.Tooltip | null>(null);
  const featureLayersRef = useRef<Map<number, L.Path>>(new Map());
  const hoveredWkrRef = useRef<number | null>(null);
  const selectedWkrRef = useRef<number | null>(null);

  // Keep refs current
  statsRef.current = stats;
  stepsRef.current = steps;
  clusterIdsRef.current = clusterIdsByWahlkreis;
  onClickRef.current = onWahlkreisClick;
  selectedWkrRef.current = selectedWahlkreis?.wkrNr ?? null;

  // Pre-compute bounding boxes for hit testing
  const featuresWithBBox = useMemo(() =>
    wahlkreiseData.features.map(f => ({
      wkrNr: f.properties.wkr_nr,
      wkrName: f.properties.wkr_name,
      geometry: f.geometry,
      bbox: computeBBox(f.geometry),
    })),
    [wahlkreiseData],
  );

  // Find which Wahlkreis a point falls in
  const findWahlkreis = useCallback((lat: number, lng: number) => {
    for (const f of featuresWithBBox) {
      const { bbox } = f;
      if (lng < bbox.minLng || lng > bbox.maxLng || lat < bbox.minLat || lat > bbox.maxLat) continue;
      if (pointInGeometry(lng, lat, f.geometry)) return f;
    }
    return null;
  }, [featuresWithBBox]);

  // Create the visual layer once (non-interactive — events handled via map)
  useEffect(() => {
    const pane = map.createPane('wahlkreise');
    pane.style.zIndex = '350';

    tooltipRef.current = L.tooltip({ direction: 'top', sticky: true });

    const layer = L.geoJSON(wahlkreiseData as never, {
      pane: 'wahlkreise',
      interactive: false,
      style: (feature) => {
        const wkrNr = feature?.properties?.wkr_nr as number;
        const s = statsRef.current.get(wkrNr);
        return {
          fillColor: getChoroplethColor(s?.totalVolume ?? 0, stepsRef.current),
          fillOpacity: 1,
          color: '#181C44',
          weight: 0.5,
          opacity: 0.2,
        };
      },
      onEachFeature: (feature, featureLayer) => {
        const wkrNr = feature?.properties?.wkr_nr as number;
        if (wkrNr != null) featureLayersRef.current.set(wkrNr, featureLayer as L.Path);
      },
    });

    layer.addTo(map);
    layerRef.current = layer;

    const coarse = isCoarsePointer();
    let touchTooltipTimer: ReturnType<typeof setTimeout> | undefined;

    const buildWkrTooltip = (wkrNr: number, wkrName: string) => {
      const s = statsRef.current.get(wkrNr);
      const volumeStr = s && s.totalVolume > 0 ? `<br>${formatInvestmentVolume(s.totalVolume)}` : '';
      return `<strong>${wkrName}</strong><br>${t().map.investments(s ? s.count : 0)}${volumeStr}`;
    };

    // Map-level event handlers (bypass canvas interception)
    const onMapClick = (e: L.LeafletMouseEvent) => {
      if (bubbleHoveredRef.current) return;
      const hit = findWahlkreis(e.latlng.lat, e.latlng.lng);
      if (hit) {
        const ids = clusterIdsRef.current.get(hit.wkrNr) ?? [];
        // On touch there's no hover (#2): the mousemove tooltip never fires, so
        // a tap would filter a district without ever naming it. Surface its
        // stats on tap and auto-dismiss so it can't stick.
        if (coarse && tooltipRef.current) {
          tooltipRef.current.setLatLng(e.latlng).setContent(buildWkrTooltip(hit.wkrNr, hit.wkrName));
          if (!map.hasLayer(tooltipRef.current)) tooltipRef.current.addTo(map);
          if (touchTooltipTimer) clearTimeout(touchTooltipTimer);
          touchTooltipTimer = setTimeout(() => tooltipRef.current?.remove(), 2600);
        }
        onClickRef.current(hit.wkrNr, hit.wkrName, ids);
      }
    };

    const onMapMouseMove = (e: L.LeafletMouseEvent) => {
      // Suppress Wahlkreis tooltip when a bubble marker is hovered
      if (bubbleHoveredRef.current) {
        if (hoveredWkrRef.current != null) {
          const fl = featureLayersRef.current.get(hoveredWkrRef.current);
          if (fl && hoveredWkrRef.current !== selectedWkrRef.current) fl.setStyle({ weight: 0.5, opacity: 0.2 });
          tooltipRef.current?.remove();
          hoveredWkrRef.current = null;
        }
        return;
      }
      const hit = findWahlkreis(e.latlng.lat, e.latlng.lng);
      const hitNr = hit?.wkrNr ?? null;
      const prevNr = hoveredWkrRef.current;

      if (hitNr === prevNr) {
        // Same wahlkreis — just update tooltip position
        if (hitNr != null) tooltipRef.current?.setLatLng(e.latlng);
        return;
      }

      // Unhighlight previous
      if (prevNr != null && prevNr !== selectedWkrRef.current) {
        const fl = featureLayersRef.current.get(prevNr);
        if (fl) fl.setStyle({ weight: 0.5, opacity: 0.2 });
      }
      tooltipRef.current?.remove();

      // Highlight new
      if (hit) {
        tooltipRef.current!.setLatLng(e.latlng).setContent(buildWkrTooltip(hit.wkrNr, hit.wkrName));
        if (!map.hasLayer(tooltipRef.current!)) tooltipRef.current!.addTo(map);

        if (hitNr !== selectedWkrRef.current) {
          const fl = featureLayersRef.current.get(hit.wkrNr);
          if (fl) fl.setStyle({ weight: 2, opacity: 0.6 });
        }
      }
      hoveredWkrRef.current = hitNr;
    };

    map.on('click', onMapClick);
    map.on('mousemove', onMapMouseMove);

    return () => {
      if (touchTooltipTimer) clearTimeout(touchTooltipTimer);
      map.off('click', onMapClick);
      map.off('mousemove', onMapMouseMove);
      map.removeLayer(layer);
      tooltipRef.current?.remove();
      layerRef.current = null;
      featureLayersRef.current.clear();
    };
  }, [map, wahlkreiseData, findWahlkreis]);

  // Update fill colors when stats or quantile steps change
  useEffect(() => {
    if (!layerRef.current) return;
    layerRef.current.setStyle((feature) => {
      const wkrNr = feature?.properties?.wkr_nr as number;
      const s = stats.get(wkrNr);
      return {
        fillColor: getChoroplethColor(s?.totalVolume ?? 0, steps),
        fillOpacity: 1,
        color: '#181C44',
        weight: 0.5,
        opacity: 0.2,
      };
    });
  }, [stats, steps]);

  // Highlight selected Wahlkreis
  useEffect(() => {
    // Reset all to default border
    for (const [, fl] of featureLayersRef.current) {
      fl.setStyle({ weight: 0.5, opacity: 0.2 });
    }
    // Highlight selected
    if (selectedWahlkreis) {
      const fl = featureLayersRef.current.get(selectedWahlkreis.wkrNr);
      if (fl) {
        fl.setStyle({ weight: 3, opacity: 0.8, color: '#181C44' });
        fl.bringToFront();
      }
    }
  }, [selectedWahlkreis]);

  return null;
}

/** Imperative marker layer — renders points with zoom-dependent type-grouped aggregation */
function ImperativeMarkers({
  points,
  maxVolume,
  bubbleMode,
  selectedClusterId,
  aggregateClusterIds,
  onBubbleClick,
  onAggregateClick,
  bubbleHoveredRef,
}: {
  points: MapPoint[];
  maxVolume: number;
  bubbleMode: 'cluster' | 'single';
  selectedClusterId: number | null;
  aggregateClusterIds: number[] | null;
  onBubbleClick: (point: MapPoint) => void;
  onAggregateClick: (clusterIds: number[]) => void;
  bubbleHoveredRef: React.MutableRefObject<boolean>;
}) {
  const map = useMap();
  const layerGroupRef = useRef<L.LayerGroup | null>(null);
  const markersRef = useRef<Map<string, { marker: L.CircleMarker; clusterId: number | null; baseColor: string }>>(new Map());
  const clusterToKeysRef = useRef<Map<number, string[]>>(new Map());
  const prevSelectedRef = useRef<number | null>(null);
  const selectedRef = useRef<number | null>(selectedClusterId);
  const sharedTooltipRef = useRef<L.Tooltip | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Touch only: the bubble whose tooltip is currently "armed". First tap on a
  // bubble shows its tooltip and arms it; a second tap on the same bubble opens
  // the modal. Cleared when the tooltip goes away (auto-dismiss / map tap), so a
  // later tap counts as a fresh first tap. Unused on fine pointers.
  const previewedKeyRef = useRef<string | null>(null);
  const aggMarkersRef = useRef<Map<string, { marker: L.CircleMarker; clusterIds: number[]; baseColor: string }>>(new Map());
  const prevAggregateRef = useRef<number[] | null>(null);
  const aggregateRef = useRef<number[] | null>(aggregateClusterIds);
  const renderRef = useRef<() => void>(() => {});

  selectedRef.current = selectedClusterId;
  aggregateRef.current = aggregateClusterIds;

  const maxVol = volumeToMillions(maxVolume) || 1;

  const getBubbleRadius = useCallback((vol: number | null) => {
    const coarse = isCoarsePointer();
    // Scale bubbles by the map's pixel width so they keep a similar density on a
    // narrow phone map as on a wide desktop one (fixed-pixel bubbles overlap far
    // sooner on the smaller mobile canvas, where all of Germany is squeezed into
    // ~360px). 600px is the reference desktop column width; clamp so the largest
    // screens are unchanged and the smallest still shrink meaningfully.
    const sizeFactor = Math.min(1, Math.max(0.4, map.getSize().x / 600));
    // #5: a 5px radius (10px target) is too small to tap on touch. On coarse
    // pointers scale the floor with the canvas too — but never below a 6px tap
    // floor — so the constant offset stops dominating (and overlapping) on the
    // dense mobile view while single-location markers stay reachable. Desktop
    // keeps its fixed 5px floor.
    const floor = coarse ? Math.max(6, 8 * sizeFactor) : 5;
    const v = volumeToMillions(vol);
    if (v <= 0) return floor;
    // Compress the volume-driven range on touch (18 vs 25) so the largest
    // bubbles don't swamp dense clusters (Ruhr, Berlin, Munich) on a small map.
    const base = coarse ? 18 : 25;
    return Math.sqrt(v / maxVol) * base * sizeFactor + floor;
  }, [maxVol, map]);

  // Keep renderRef up to date with latest render logic
  renderRef.current = () => {
    if (!layerGroupRef.current) {
      layerGroupRef.current = L.layerGroup().addTo(map);
    } else {
      layerGroupRef.current.clearLayers();
    }

    // Custom tooltip rendered outside leaflet-container to avoid overflow/z-index issues
    if (!sharedTooltipRef.current) {
      const el = document.createElement('div');
      el.style.cssText = `
        position: absolute; z-index: 1100; pointer-events: none;
        background: white; color: #333; padding: 6px 10px;
        border-radius: 4px; font-size: 12px; line-height: 1.4;
        font-family: ${theme.fontFamily};
        box-shadow: 0 1px 4px rgba(0,0,0,0.3);
        white-space: nowrap; display: none; transform: translate(-50%, -100%);
        margin-top: -8px;
      `;
      map.getContainer().parentElement!.appendChild(el);
      sharedTooltipRef.current = el as unknown as L.Tooltip;
    }
    const tooltipEl = sharedTooltipRef.current as unknown as HTMLDivElement;
    const coarse = isCoarsePointer();

    const hideTooltip = () => {
      tooltipEl.style.display = 'none';
      // On touch, mouseout is unreliable, so bubbleHoveredRef (which gates
      // Wahlkreis clicks) would stay stuck true after the first bubble tap.
      // Clearing it here — wherever the tooltip goes away — keeps the gate honest.
      bubbleHoveredRef.current = false;
      // Disarm the two-tap preview: once the tooltip is gone, the next tap on a
      // bubble should re-preview, not open the modal.
      previewedKeyRef.current = null;
      if (tooltipTimerRef.current) { clearTimeout(tooltipTimerRef.current); tooltipTimerRef.current = null; }
    };
    const showTooltip = (latlng: L.LatLngExpression, html: string) => {
      const pt = map.latLngToContainerPoint(latlng as L.LatLngTuple);
      tooltipEl.innerHTML = html;
      tooltipEl.style.left = `${pt.x}px`;
      tooltipEl.style.top = `${pt.y}px`;
      tooltipEl.style.display = 'block';
      // #3: on touch a tap fires synthesized mouseover (shows the tooltip) but
      // no reliable mouseout, so it sticks over the map. Auto-dismiss after a
      // beat on coarse pointers; desktop keeps the hover-driven show/hide.
      if (coarse) {
        if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
        // Long enough to comfortably land the deliberate second tap that opens
        // the modal before the preview disarms itself.
        tooltipTimerRef.current = setTimeout(hideTooltip, 3500);
      }
    };

    const zoom = map.getZoom();
    const { aggregates, singles } = bubbleMode === 'cluster'
      ? clusterByTypeAndGrid(points, zoom)
      : { aggregates: [] as AggregateCluster[], singles: points };
    const sel = selectedRef.current;

    const newMarkers = new Map<string, { marker: L.CircleMarker; clusterId: number | null; baseColor: string }>();
    const newClusterToKeys = new Map<number, string[]>();
    const newAggMarkers = new Map<string, { marker: L.CircleMarker; clusterIds: number[]; baseColor: string }>();

    // Build set of active aggregate cluster IDs for highlight check
    const activeAggSet = new Set(aggregateRef.current || []);

    // Sort aggregates by volume descending (large behind, small on top)
    aggregates.sort((a, b) => b.totalVolume - a.totalVolume);

    for (const agg of aggregates) {
      const color = getTypeColor(agg.type);
      const radius = getBubbleRadius(agg.totalVolume);
      const aggClusterIds = [...new Set(agg.points.map(p => p.cluster_id).filter((id): id is number => id != null))];
      const isHighlighted = activeAggSet.size > 0 && aggClusterIds.some(id => activeAggSet.has(id));

      const marker = L.circleMarker([agg.lat, agg.lng], {
        radius,
        fillColor: color,
        fillOpacity: isHighlighted ? 0.85 : 0.7,
        color: isHighlighted ? '#181C44' : color,
        weight: isHighlighted ? 3 : 1.5,
        opacity: isHighlighted ? 1 : 0.8,
      });

      const volumeStr = agg.totalVolume > 0
        ? ` — ${formatInvestmentVolume(agg.totalVolume)}`
        : '';
      // 'Other' is the grouping key for rows without a type, not display text.
      const typeText = agg.type === 'Other' ? otherLabel('Sonstige') : typeLabel(agg.type);
      const content = `<strong>${agg.count} × ${typeText}</strong>${volumeStr}`;

      marker.on('mouseover', () => {
        bubbleHoveredRef.current = true;
        showTooltip([agg.lat, agg.lng], content);
      });
      marker.on('mouseout', () => { bubbleHoveredRef.current = false; hideTooltip(); });
      marker.on('click', () => {
        // Touch: first tap only previews (tooltip); a second tap on the same
        // bubble drills in. Desktop opens immediately on click.
        if (coarse && previewedKeyRef.current !== agg.key) {
          previewedKeyRef.current = agg.key;
          showTooltip([agg.lat, agg.lng], content);
          return;
        }
        if (aggClusterIds.length > 0) onAggregateClick(aggClusterIds);
      });

      layerGroupRef.current!.addLayer(marker);
      newAggMarkers.set(agg.key, { marker, clusterIds: aggClusterIds, baseColor: color });
    }

    // Sort singles by volume descending
    const sortedSingles = [...singles].sort(
      (a, b) => (b.investment_volume_value_eur ?? 0) - (a.investment_volume_value_eur ?? 0),
    );

    for (const p of sortedSingles) {
      const color = getTypeColor(p.type_of_investment);
      const radius = getBubbleRadius(p.investment_volume_value_eur);
      const isSelected = p.cluster_id != null && p.cluster_id === sel;
      const key = `${p.announcement_id}-${p.location_id}`;
      // Financial transactions are anonymised everywhere else (table shows
      // industry instead of company, rows aren't clickable) — same here.
      const isFinancial = FINANCIAL_TYPES.includes(p.type_of_investment || '');

      const marker = L.circleMarker([p.latitude, p.longitude], {
        className: isFinancial ? 'financial-bubble' : undefined,
        radius,
        fillColor: color,
        fillOpacity: isSelected ? 0.85 : 0.7,
        color: isSelected ? '#181C44' : color,
        weight: isSelected ? 3 : 1.5,
        opacity: isSelected ? 1 : 0.8,
      });

      const volumeStr = p.investment_volume_value_eur != null
        ? `<br>${formatInvestmentVolume(p.investment_volume_value_eur)}`
        : '';
      // Built on hover rather than up front, so the English industry names
      // apply even when they land after the markers were drawn.
      const content = () => {
        const place = `${p.city_name || ''}, ${p.federal_state ? stateLabel(p.federal_state) : ''}${volumeStr}`;
        return isFinancial
          ? `<strong>${p.primary_industry ? industryLabel(p.primary_industry) : ''}</strong><br>${place}`
          : `<strong>${p.investing_company || ''}</strong><br>${p.project_name || ''}<br>${place}`;
      };

      marker.on('mouseover', () => {
        bubbleHoveredRef.current = true;
        showTooltip([p.latitude, p.longitude], content());
      });
      marker.on('mouseout', () => { bubbleHoveredRef.current = false; hideTooltip(); });
      marker.on('click', () => {
        // Touch: first tap only previews (tooltip); a second tap on the same
        // bubble opens the modal. Desktop opens immediately on click.
        // Financial bubbles never open the modal: the tap just shows the tooltip.
        if (isFinancial || (coarse && previewedKeyRef.current !== key)) {
          previewedKeyRef.current = key;
          showTooltip([p.latitude, p.longitude], content());
          return;
        }
        onBubbleClick(p);
      });

      layerGroupRef.current!.addLayer(marker);
      newMarkers.set(key, { marker, clusterId: p.cluster_id, baseColor: color });

      if (p.cluster_id != null) {
        let keys = newClusterToKeys.get(p.cluster_id);
        if (!keys) {
          keys = [];
          newClusterToKeys.set(p.cluster_id, keys);
        }
        keys.push(key);
      }
    }

    markersRef.current = newMarkers;
    clusterToKeysRef.current = newClusterToKeys;
    aggMarkersRef.current = newAggMarkers;
    prevSelectedRef.current = sel;
    prevAggregateRef.current = aggregateRef.current;
  };

  // Mount effect: initial render + moveend listener
  useEffect(() => {
    renderRef.current();

    const onMoveEnd = () => renderRef.current();
    map.on('moveend', onMoveEnd);
    // Re-render on resize too so the width-proportional bubble radius updates
    // when the map's pixel size changes (e.g. a phone rotating to landscape).
    map.on('resize', onMoveEnd);

    return () => {
      map.off('moveend', onMoveEnd);
      map.off('resize', onMoveEnd);
      if (tooltipTimerRef.current) { clearTimeout(tooltipTimerRef.current); tooltipTimerRef.current = null; }
      if (layerGroupRef.current) {
        layerGroupRef.current.clearLayers();
        map.removeLayer(layerGroupRef.current);
        layerGroupRef.current = null;
      }
      if (sharedTooltipRef.current) {
        sharedTooltipRef.current.remove();
        sharedTooltipRef.current = null;
      }
    };
  }, [map, points, getBubbleRadius, bubbleMode]);

  // Targeted selection effect — O(1) style updates instead of full re-render
  useEffect(() => {
    const prev = prevSelectedRef.current;
    const curr = selectedClusterId;
    if (prev === curr) return;

    // Deselect previous
    if (prev != null) {
      const keys = clusterToKeysRef.current.get(prev) || [];
      for (const key of keys) {
        const entry = markersRef.current.get(key);
        if (!entry) continue;
        entry.marker.setStyle({
          fillOpacity: 0.7,
          color: entry.baseColor,
          weight: 1.5,
          opacity: 0.8,
        });
      }
    }

    // Select current
    if (curr != null) {
      const keys = clusterToKeysRef.current.get(curr) || [];
      for (const key of keys) {
        const entry = markersRef.current.get(key);
        if (!entry) continue;
        entry.marker.setStyle({
          fillOpacity: 0.85,
          color: '#181C44',
          weight: 3,
          opacity: 1,
        });
      }
    }

    prevSelectedRef.current = curr;
  }, [selectedClusterId]);

  // Targeted aggregate highlight effect — O(n) style updates instead of full re-render
  useEffect(() => {
    const prev = prevAggregateRef.current;
    const curr = aggregateClusterIds;
    if (prev === curr) return;

    const prevSet = new Set(prev || []);
    const currSet = new Set(curr || []);

    // Update each aggregate marker's style
    for (const [, entry] of aggMarkersRef.current) {
      const wasSel = prevSet.size > 0 && entry.clusterIds.some(id => prevSet.has(id));
      const isSel = currSet.size > 0 && entry.clusterIds.some(id => currSet.has(id));
      if (wasSel === isSel) continue;

      if (isSel) {
        entry.marker.setStyle({
          fillOpacity: 0.85,
          color: '#181C44',
          weight: 3,
          opacity: 1,
        });
      } else {
        entry.marker.setStyle({
          fillOpacity: 0.7,
          color: entry.baseColor,
          weight: 1.5,
          opacity: 0.8,
        });
      }
    }

    prevAggregateRef.current = curr;
  }, [aggregateClusterIds]);

  return null;
}

interface InvestmentMapProps {
  points: MapPoint[];
  maxVolume: number;
  bubbleMode: 'off' | 'cluster' | 'single';
  geoJsonData: GeoJsonFeatureCollection | null;
  wahlkreiseData: WahlkreiseGeoJson | null;
  selectedClusterId: number | null;
  selectionSourceRef: React.MutableRefObject<'bubble' | 'table' | null>;
  aggregateClusterIds: number[] | null;
  selectedWahlkreis: { wkrNr: number; name: string } | null;
  investmentTypeFilter: string[];
  activeTiers: { physical: boolean; financial: boolean };
  availableTypes: { value: string; label: string; count: number }[];
  onBubbleClick: (point: MapPoint) => void;
  onAggregateClick: (clusterIds: number[]) => void;
  onWahlkreisClick: (wkrNr: number, name: string, clusterIds: number[]) => void;
  onTypeFilterChange: (types: string[]) => void;
  mapRef: React.MutableRefObject<LeafletMap | null>;
}

export const InvestmentMap = memo(function InvestmentMap({ points, maxVolume, bubbleMode, geoJsonData, wahlkreiseData, selectedClusterId, selectionSourceRef, aggregateClusterIds, selectedWahlkreis, investmentTypeFilter, activeTiers, availableTypes, onBubbleClick, onAggregateClick, onWahlkreisClick, onTypeFilterChange, mapRef }: InvestmentMapProps) {
  const m = useT();
  // Compute wahlkreis stats + quantile steps at parent level (shared by choropleth layer + legend)
  const wahlkreisStats = useMemo(() => {
    const clusters = new Map<number, Set<number | null>>();
    const volumes = new Map<number, number>();
    for (const p of points) {
      if (p.wahlkreis_nr == null) continue;
      let set = clusters.get(p.wahlkreis_nr);
      if (!set) {
        set = new Set();
        clusters.set(p.wahlkreis_nr, set);
        volumes.set(p.wahlkreis_nr, 0);
      }
      if (!set.has(p.cluster_id)) {
        set.add(p.cluster_id);
        volumes.set(p.wahlkreis_nr, volumes.get(p.wahlkreis_nr)! + (p.investment_volume_value_eur ?? 0));
      }
    }
    const m = new Map<number, WahlkreisStats>();
    for (const [wkr, set] of clusters) {
      m.set(wkr, { count: set.size, totalVolume: volumes.get(wkr)! });
    }
    return m;
  }, [points]);

  const choroplethSteps = useMemo(() => computeEquidistantSteps(wahlkreisStats), [wahlkreisStats]);

  // Map wahlkreis_nr → unique cluster_ids (for click-to-filter)
  const clusterIdsByWahlkreis = useMemo(() => {
    const m = new Map<number, number[]>();
    const seen = new Map<number, Set<number>>();
    for (const p of points) {
      if (p.wahlkreis_nr == null || p.cluster_id == null) continue;
      let set = seen.get(p.wahlkreis_nr);
      if (!set) {
        set = new Set();
        seen.set(p.wahlkreis_nr, set);
        m.set(p.wahlkreis_nr, []);
      }
      if (!set.has(p.cluster_id)) {
        set.add(p.cluster_id);
        m.get(p.wahlkreis_nr)!.push(p.cluster_id);
      }
    }
    return m;
  }, [points]);

  const bubbleHoveredRef = useRef(false);

  const [isFullscreen, setIsFullscreen] = useState(false);

  // Types the on-screen legend shows — the exported image redraws the same set.
  const visibleLegendTypes = ALL_INVESTMENT_TYPES.filter(t => {
    if (PHYSICAL_TYPES.includes(t)) return activeTiers.physical;
    if (FINANCIAL_TYPES.includes(t)) return activeTiers.financial;
    return false;
  });

  // Guards double-clicks while the PNG is being encoded.
  const isExportingRef = useRef(false);
  const handleExportImage = async () => {
    const map = mapRef.current;
    if (!map || isExportingRef.current) return;
    isExportingRef.current = true;
    try {
      await exportMapImage(
        map,
        GERMANY_BOUNDS,
        bubbleMode !== 'off'
          ? {
              title: m.map.typeLegend,
              entries: visibleLegendTypes.map(type => ({
                label: typeLabel(type),
                color: getTypeColor(type),
                dimmed: !(investmentTypeFilter.length === 0 || investmentTypeFilter.includes(type)),
              })),
            }
          : null,
        wahlkreiseData && choroplethSteps.length > 0
          ? {
              title: m.map.volumeLegend,
              entries: choroplethSteps.map(step => ({ label: step.label, color: step.color, swatch: true })),
            }
          : null,
      );
    } catch (error) {
      console.error('Map image export failed:', error);
    } finally {
      isExportingRef.current = false;
    }
  };

  // Phone-only legend collapse. Both legends sit in the map's bottom corners and
  // covered roughly the bottom third of the small mobile map; they start
  // collapsed (single-line chips) and expand on tap. Ignored on desktop, where
  // CSS always renders the bodies (see LegendBody).
  const [typeLegendOpen, setTypeLegendOpen] = useState(false);
  const [volLegendOpen, setVolLegendOpen] = useState(false);

  // While fullscreen: lock background scroll, close on Escape, and route the
  // browser/Android back gesture to exit fullscreen (push a history entry on
  // enter, pop it on a button/Escape exit; a popstate exit means the entry is
  // already gone, so don't pop again).
  useEffect(() => {
    if (!isFullscreen) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    let poppedByUser = false;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsFullscreen(false);
    };
    const onPopState = () => { poppedByUser = true; setIsFullscreen(false); };

    window.history.pushState({ mapFullscreen: true }, '');
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('popstate', onPopState);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('popstate', onPopState);
      // Drop the history entry we pushed, unless the back gesture already did.
      if (!poppedByUser) window.history.back();
    };
  }, [isFullscreen]);

  return (
    <MapWrapper $fullscreen={isFullscreen}>
      <MapContainer
        bounds={GERMANY_BOUNDS}
        boundsOptions={{ padding: [8, 8] }}
        scrollWheelZoom={true}
        preferCanvas={true}
        // zoomSnap:0 lets fitBounds settle on a *fractional* zoom that fills
        // GERMANY_BOUNDS exactly. With the default (zoomSnap:1) fitBounds floors
        // to an integer level, and Germany sits right on the z5/z6 boundary at
        // this container size — so a few px of width/height difference between
        // the host page (~684x549 → z5, all of Europe) and the dev harness
        // (~708x590 → z6, Germany fills) flips a whole zoom level. Fractional
        // zoom makes the framing identical regardless of the exact container.
        zoomSnap={0}
        // With the raster TileLayer gone no layer caps the zoom anymore
        // (GridLayer used to impose its default maxZoom of 18); the Positron
        // vector style overzooms fine to 18.
        maxZoom={18}
        ref={mapRef}
        // Positron ground tone as the container background, so the pre-load
        // flash and the WebGL-less fallback match the basemap (and the PNG
        // export's ground fill).
        style={{ height: '100%', minHeight: '500px', backgroundColor: '#d4dadc' }}
      >
        <GlBasemapLayer />

        {geoJsonData && (
          <GeoJSON data={geoJsonData} style={() => geoJsonStyle} />
        )}

        {wahlkreiseData && (
          <WahlkreiseChoropleth
            wahlkreiseData={wahlkreiseData}
            stats={wahlkreisStats}
            steps={choroplethSteps}
            clusterIdsByWahlkreis={clusterIdsByWahlkreis}
            selectedWahlkreis={selectedWahlkreis}
            onWahlkreisClick={onWahlkreisClick}
            bubbleHoveredRef={bubbleHoveredRef}
          />
        )}

        {bubbleMode !== 'off' && (
          <ImperativeMarkers
            points={points}
            maxVolume={maxVolume}
            bubbleMode={bubbleMode}
            selectedClusterId={selectedClusterId}
            aggregateClusterIds={aggregateClusterIds}
            onBubbleClick={onBubbleClick}
            onAggregateClick={onAggregateClick}
            bubbleHoveredRef={bubbleHoveredRef}
          />
        )}

        <MapControllerSimple points={points} selectedClusterId={selectedClusterId} selectionSourceRef={selectionSourceRef} />

        <MapFitController />

        <TouchGestureGuard fullscreen={isFullscreen} />
      </MapContainer>
      <FullscreenButton
        type="button"
        onClick={() => setIsFullscreen(f => !f)}
        aria-label={isFullscreen ? m.map.exitFullscreen : m.map.enterFullscreen}
        title={isFullscreen ? m.map.exitFullscreen : m.map.enterFullscreen}
        aria-pressed={isFullscreen}
      >
        {isFullscreen ? (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
            <path d="M16 3h3a2 2 0 0 1 2 2v3" />
            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
            <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
          </svg>
        )}
      </FullscreenButton>
      <MapExportButton
        type="button"
        onClick={handleExportImage}
        title={m.map.exportImage}
        aria-label={m.map.exportImage}
      >
        <span className="icon" data-icon="download" />
      </MapExportButton>
      {bubbleMode !== 'off' && (
        <LegendContainer>
          <LegendHeader
            type="button"
            onClick={() => setTypeLegendOpen(o => !o)}
            aria-expanded={typeLegendOpen}
          >
            <LegendTitle>{m.map.typeLegend}</LegendTitle>
            <LegendChevron $open={typeLegendOpen} aria-hidden="true">▾</LegendChevron>
          </LegendHeader>
          <LegendBody $open={typeLegendOpen}>
          {visibleLegendTypes.map(type => {
            const isActive = investmentTypeFilter.length === 0 || investmentTypeFilter.includes(type);
            return (
              <LegendItem
                key={type}
                dimmed={!isActive}
                onClick={() => {
                  const tierTypes = ALL_INVESTMENT_TYPES.filter(t =>
                    (activeTiers.physical && PHYSICAL_TYPES.includes(t)) || (activeTiers.financial && FINANCIAL_TYPES.includes(t))
                  );
                  const allActive = investmentTypeFilter.length === 0 || tierTypes.every(t => investmentTypeFilter.includes(t));
                  if (allActive) {
                    onTypeFilterChange([type]);
                  } else if (investmentTypeFilter.includes(type)) {
                    const next = investmentTypeFilter.filter(t => t !== type);
                    onTypeFilterChange(next.length > 0 ? next : tierTypes);
                  } else {
                    const next = [...investmentTypeFilter, type];
                    const allBack = tierTypes.every(t => next.includes(t));
                    onTypeFilterChange(allBack ? tierTypes : next);
                  }
                }}
                title={m.map.onlyThisType}
              >
                <LegendDot color={getTypeColor(type)} />
                {typeLabel(type)}
              </LegendItem>
            );
          })}
          </LegendBody>
        </LegendContainer>
      )}
      {wahlkreiseData && choroplethSteps.length > 0 && (
        <ChoroplethLegendContainer>
          <LegendHeader
            type="button"
            onClick={() => setVolLegendOpen(o => !o)}
            aria-expanded={volLegendOpen}
          >
            <LegendTitle>{m.map.volumeLegend}</LegendTitle>
            <LegendChevron $open={volLegendOpen} aria-hidden="true">▾</LegendChevron>
          </LegendHeader>
          <LegendBody $open={volLegendOpen}>
          {choroplethSteps.map(step => (
            <LegendItem key={step.label}>
              <LegendSwatch bg={step.color} />
              {step.label}
            </LegendItem>
          ))}
          </LegendBody>
        </ChoroplethLegendContainer>
      )}
    </MapWrapper>
  );
});
